// Pixel-art helpers for Turkey Hunt. Everything is drawn at the game's
// internal resolution (480x270) and upscaled with `image-rendering: pixelated`.

export const W = 480;
export const H = 270;

export interface Layer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

export function makeLayer(w: number, h: number): Layer {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  return { canvas, ctx };
}

export function rect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, col: string) {
  ctx.fillStyle = col;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

export function px(ctx: CanvasRenderingContext2D, x: number, y: number, col: string) {
  ctx.fillStyle = col;
  ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
}

/** Hard-edged filled ellipse (scanline fill, no anti-aliasing). */
export function ellipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number, col: string) {
  ctx.fillStyle = col;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    const dy = (y + 0.5 - cy) / ry;
    if (Math.abs(dy) > 1) continue;
    const hw = rx * Math.sqrt(1 - dy * dy);
    const x0 = Math.round(cx - hw);
    const x1 = Math.round(cx + hw);
    if (x1 > x0) ctx.fillRect(x0, y, x1 - x0, 1);
  }
}

/** Filled polygon, then alpha thresholded so edges stay crisp. */
export function poly(ctx: CanvasRenderingContext2D, pts: [number, number][], col: string) {
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  ctx.fill();
}

/** Make every pixel fully opaque or fully transparent (pixel-art crispness). */
export function threshold(layer: Layer) {
  const { canvas, ctx } = layer;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  for (let i = 3; i < d.length; i += 4) d[i] = d[i] >= 110 ? 255 : 0;
  ctx.putImageData(img, 0, 0);
}

/** Deterministic hash noise in [0,1). */
export function noise(x: number, y: number, seed = 0): number {
  let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return ((h >>> 0) % 10000) / 10000;
}

/** Ordered 2x2 dither test, for banded gradients. */
export function dither(x: number, y: number, t: number): boolean {
  const m = [0.125, 0.625, 0.875, 0.375][(x & 1) + ((y & 1) << 1)];
  return t > m;
}

/** Alpha mask (1 = opaque) of a full-frame layer. */
export function alphaMask(layer: Layer): Uint8Array {
  const { canvas, ctx } = layer;
  const d = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  const m = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0, j = 3; i < m.length; i++, j += 4) m[i] = d[j] > 0 ? 1 : 0;
  return m;
}

// 3x5 digits for the turkey number badges.
const DIGITS: Record<string, string[]> = {
  "1": ["010", "110", "010", "010", "111"],
  "2": ["110", "001", "010", "100", "111"],
  "3": ["110", "001", "010", "001", "110"],
};

export function digit(ctx: CanvasRenderingContext2D, d: string, x: number, y: number, col: string) {
  const g = DIGITS[d];
  if (!g) return;
  ctx.fillStyle = col;
  for (let r = 0; r < 5; r++) for (let c = 0; c < 3; c++) if (g[r][c] === "1") ctx.fillRect(x + c, y + r, 1, 1);
}
